import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { EditMode } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfileRelationshipsHandler extends BaseOperationHandler {
    constructor() {
        super();
        this.RegisterOperation(LocalPatientOperations.Profile.Relationships_EditClick, this.editRelationshipClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Relationships_SaveClick, this.saveRelationshipClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Relationships_CancelClick, this.cancelRelationshipClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Relationships_RemoveClick, this.removeRelationshipClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Relationships_RelationshipLinkedContactMutation, this.changeLinkedContact);
        this.RegisterOperation(LocalPatientOperations.Profile.Relationships_AddRelation, this.addRelation);
    }

    private changeLinkedContact(request: LocalEvent, store: PatientStore): void {
        const linkedContactId = request.Get<string>("linkedContactId");
        const mutation = request.Get<(lc: LinkedContact) => void>("mutation");

        const model = patientStore.PatientProfileRelationshipsModels[request.PatientId];
        const relations = model
            .get("Relationships")
            .map(x => {
                let newLinkedContact = x;
                if (x.Id === linkedContactId) {
                    newLinkedContact = JSON.parse(JSON.stringify(x));
                    mutation(newLinkedContact);
                }
                return newLinkedContact;
            })
            .toArray()
            .toImmutable();

        store.PatientProfileRelationshipsModels[request.PatientId] = model.set("Relationships", relations);
    }

    private editRelationshipClick(request: LocalEvent, store: PatientStore): void {
        // undo model (to get copy/clone of immutable model)
        const modelToUndo = patientStore.PatientProfileRelationshipsModels[request.PatientId];
        store.PatientProfileRelationshipsUndoModels[request.PatientId] = modelToUndo
            .set("PatientContactId", modelToUndo.get("PatientContactId"));

        // UI model
        store.PatientUIModels[request.PatientId] = store.PatientUIModels[request.PatientId]
            .set("PatientProfileEditMode", EditMode.Edit);
    }

    private saveRelationshipClick(request: LocalEvent, store: PatientStore): void {
        const uiModel = store.PatientUIModels[request.PatientId];
        const relationshipsModel = store.PatientProfileRelationshipsModels[request.PatientId];

        store.ActionsProfile.savePatientRelationships(uiModel, relationshipsModel);
    }

    public cancelRelationshipClick(request: LocalEvent, store: PatientStore): void {
        const undoModel = store.PatientProfileRelationshipsUndoModels[request.PatientId]
            .set("ShowRelationshipPopup", false)
            .set("ShowRelationshipExternalPopup", false);

        const uiModel = store.PatientUIModels[request.PatientId]
            .set("PatientProfileEditMode", EditMode.View);

        delete store.PatientProfileRelationshipsUndoModels[request.PatientId];
        store.PatientProfileRelationshipsModels[request.PatientId] = undoModel;
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    private removeRelationshipClick(request: LocalEvent, store: PatientStore): void {
        const linkedContactId = request.Get<string>("linkedContactId");
        const index = request.Get<number>("itemIndex");

        let model = store.PatientProfileRelationshipsModels[request.PatientId];
        if (!IsNullOrWhiteSpace(linkedContactId)) {
            // pile up the Id to send to the server for delete
            const idsToDelete = model.get("DeleteRelationshipIds").toArray();
            idsToDelete.push(linkedContactId);
            model = model.set("DeleteRelationshipIds", idsToDelete.toImmutable());
        }

        // remove it from the Client
        model = model.set("Relationships", model.get("Relationships").toArray().filter((x, idx) => idx !== index).toImmutable());

        store.PatientProfileRelationshipsModels[request.PatientId] = model;
    }

    private addRelation(request: LocalEvent, store: PatientStore): void {
        let model = patientStore.PatientProfileRelationshipsModels[request.PatientId];
        const newRelationship = request.Get<LinkedContact>("linkedContact");
        const currentRels = model.get("Relationships").toArray();
        currentRels.push(newRelationship);
        model = model.set("Relationships", Immutable.List(currentRels));
        store.PatientProfileRelationshipsModels[request.PatientId] = model;

        // Save to server
        const uiModel = store.PatientUIModels[request.PatientId];
        store.ActionsProfile.savePatientRelationships(uiModel, model, true);
    }
}

export { PatientProfileRelationshipsHandler };
